import {
    IProps,
} from './types';

import classNames from 'classnames';
import React, {
    FC,
    ReactNode,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    createPortal,
} from 'react-dom';

import {
    getTooltipPosition,
} from './functions';

import styles from './Tooltip.module.scss';

function formatTooltipContent(content: ReactNode): ReactNode {
    if (typeof content !== 'string') {
        return content;
    }

    return content
        .split('\n')
        .map((line, i) => (
            <div key={i}>
                {line}
            </div>
        ));
}

const Tooltip: FC<IProps> = (props) => {
    const [hoveredDOMRect, setHoveredDOMRect] = useState<DOMRect | null>(null);
    const [position, setPosition] = useState<{top: number; left: number} | null>(null);

    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const tooltipContent = useMemo(() => {
        return formatTooltipContent(props.content);
    }, [props.content]);

    useLayoutEffect(() => {
        if (!hoveredDOMRect || !tooltipRef.current) {
            setPosition(null);

            return;
        }

        setPosition(getTooltipPosition({
            triggerRect: hoveredDOMRect,
            tooltipSize: tooltipRef.current.getBoundingClientRect(),
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            offsetTop: props.offsetTop,
            offsetLeft: props.offsetLeft,
        }));
    }, [hoveredDOMRect, props.offsetTop, props.offsetLeft, tooltipContent]);

    const onMouseEnter = () => {
        if (props.isDisabled) {
            return;
        }

        setHoveredDOMRect(triggerRef.current?.getBoundingClientRect() ?? null);
    };
    const onMouseLeave = () => {
        setHoveredDOMRect(null);
        setPosition(null);
    };

    return (
        <div
            ref={triggerRef}
            className={classNames(styles.trigger, props.className)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {props.children}
            {
                !props.isDisabled &&
                hoveredDOMRect &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={styles.tooltip}
                        style={{
                            top: position?.top ?? hoveredDOMRect.top + (props.offsetTop ?? hoveredDOMRect.height + 4),
                            left: position?.left ?? hoveredDOMRect.left + (props.offsetLeft ?? 0),
                            visibility: position ? 'visible' : 'hidden',
                        }}
                    >
                        {tooltipContent}
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

export default Tooltip;
